import React, { useState, useEffect, useRef } from 'react';
import { Building2, User, Plus, Pencil, Trash, MapPin, Camera } from 'lucide-react';
import http from '../../config/http';
import DrawerWrapper from '../DrawerWrapper';
import { CustomTable } from '../Table/CustomTable';
import uploadFileToS3 from '../../utils/uploadFileToS3';

export default function SucursalManagement() {
  const [sucursales, setSucursales] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingSucursal, setEditingSucursal] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageError, setImageError] = useState('');
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    address: '',
    responsible: '',
    iconUrl: ''
  });

  useEffect(() => {
    fetchSucursales();
  }, []);

  const fetchSucursales = async () => {
    try {
      const response = await http.get('/api/sucursales');
      setSucursales(response.data);
    } catch (error) {
      console.error('Error fetching sucursales:', error);
    }
  };

  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  const handleImageUpload = async (file) => {
    if (!file.type.startsWith('image/')) {
      setImageError('Please select a valid image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setImageError('Image size must be less than 5MB');
      return;
    }

    setUploadingImage(true);
    setImageError('');

    try {
      const imageUrl = await uploadFileToS3({
        file,
        module: 'sucursales',
        recordId: editingSucursal?._id || 'temp-' + Date.now(),
        tenantId: 'default',
        tipo: 'icon',
      });

      setFormData(prev => ({ ...prev, iconUrl: imageUrl }));
      console.log('Sucursal icon uploaded successfully');
    } catch (error) {
      console.error('Error uploading image:', error);
      setImageError('Failed to upload image. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemovePhoto = () => {
    setFormData(prev => ({ ...prev, iconUrl: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingSucursal) {
        await http.put(`/api/sucursales/${formData._id}`, formData);
        setSucursales(prev => prev.map(s => (s._id === formData._id ? formData : s)));
      } else {
        const { data } = await http.post('/api/sucursales', formData);
        setSucursales(prev => [...prev, data]);
      }

      setDrawerOpen(false);
      setEditingSucursal(null);
      setFormData({ name: '', address: '', responsible: '', iconUrl: '' });
    } catch (error) {
      console.error('Error saving sucursal:', error);
      alert('Failed to save sucursal');
    }
  };

  const deleteSucursal = async (id) => {
    if (sucursales.length === 1) {
      alert('Cannot delete the last sucursal. At least one branch must exist.');
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this sucursal?')) {
      try {
        await http.delete(`/api/sucursales/${id}`);
        setSucursales(prev => prev.filter(s => s._id !== id));
      } catch (error) {
        console.error('Error deleting sucursal:', error);
        alert('Failed to delete sucursal');
      }
    }
  };

  const handleEditSucursal = (sucursal) => {
    setEditingSucursal(sucursal);
    setFormData({
      ...sucursal
    });
    setDrawerOpen(true);
  };

  const getInitials = (name = "") => {
    const parts = name.trim().split(" ");
    const first = parts[0]?.[0] || "";
    const second = parts[1]?.[0] || "";
    return (first + second).toUpperCase();
  };

  // Define columns for CustomTable
  const columns = [
    {
      header: "Branch",
      accessorKey: "name",
      cell: ({ row }) => {
        const sucursal = row.original;
        return (
          <div className="flex items-center gap-3">
            {sucursal.iconUrl ? (
              <img
                src={sucursal.iconUrl}
                alt={sucursal.name}
                className="w-10 h-10 rounded-lg object-cover border-2 border-gray-200"
              />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 text-white flex items-center justify-center text-sm font-semibold">
                {getInitials(sucursal.name)}
              </div>
            )}
            <div>
              <div className="text-sm font-semibold text-gray-900">{sucursal.name}</div>
            </div>
          </div>
        );
      }
    },
    {
      header: "Location",
      accessorKey: "address",
      cell: ({ row }) => (
        <div>
          <div className="text-sm text-gray-900">{row.original.address}</div>
          <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
            <MapPin className="w-3 h-3" />
            Branch location
          </div>
        </div>
      )
    },
    {
      header: "Responsible",
      accessorKey: "responsible",
      cell: ({ row }) => (
        <div>
          {row.original.responsible ? (
            <>
              <div className="text-sm text-gray-900">{row.original.responsible}</div>
              <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                <User className="w-3 h-3" />
                Manager
              </div>
            </>
          ) : (
            <span className="text-gray-400 text-sm">—</span>
          )}
        </div>
      )
    },
    {
      header: "Action",
      isHideSort: true,
      cell: ({ row }) => (
        <div className="flex gap-2">
          <button
            onClick={() => handleEditSucursal(row.original)}
            className="py-2 px-3 border shadow rounded-lg flex items-center gap-2"
            title="Edit Sucursal"
          >
            <Pencil size={14} />
          </button>
          <button
            onClick={() => deleteSucursal(row.original._id)}
            disabled={sucursales.length === 1}
            className={`py-2 px-3 border shadow rounded-lg flex items-center gap-2 ${
              sucursales.length === 1 ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            title={sucursales.length === 1 ? 'Cannot delete the last sucursal' : 'Delete Sucursal'}
          >
            <Trash size={14} />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="p-6">
      {/* Header Section */}
      <div className="mb-8">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Sucursales</h2>
          <p className="text-gray-600 mt-1">Manage your business branches and locations</p>
        </div>

        {/* Stats Cards */}
        <div className="flex items-center gap-4 flex-wrap mb-6">
          {/* Total Sucursales Card */}
          <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 shadow-sm min-w-[120px]">
            <div className="text-2xl font-bold text-gray-900">{sucursales.length}</div>
            <div className="text-sm text-gray-600">Total Branches</div>
          </div>
          
          {/* Active Sucursales */}
          <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 shadow-sm min-w-[120px]">
            <div className="text-2xl font-bold text-green-600">{sucursales.filter(s => s.responsible).length}</div>
            <div className="text-sm text-gray-600">With Manager</div>
          </div>
          
          {/* Unassigned */}
          <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 shadow-sm min-w-[120px]">
            <div className="text-2xl font-bold text-orange-600">{sucursales.filter(s => !s.responsible).length}</div>
            <div className="text-sm text-gray-600">Unassigned</div>
          </div>
        </div>
      </div>

      {/* Main Content Card with CustomTable */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <CustomTable
          data={sucursales}
          columns={columns}
          dataNotFoundQuery="No sucursales yet. Get started by adding your first branch location."
          additionalActions={
            <button
              onClick={() => {
                setEditingSucursal(null);
                setFormData({ 
                  name: '', 
                  address: '', 
                  responsible: '', 
                  iconUrl: ''
                });
                setDrawerOpen(true);
              }}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-lg font-medium text-sm shadow-sm"
            >
              <Plus className="h-4 w-4" />
              Add Sucursal
            </button>
          }
        />
      </div>

      {/* Enhanced Drawer */}
      <DrawerWrapper
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setEditingSucursal(null);
          setImageError('');
        }}
        title={editingSucursal ? 'Edit Sucursal' : 'Add New Sucursal'}
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Icon Section */}
          <div className="border-b border-gray-200 pb-6">
            <h3 className="text-sm font-medium text-gray-900 mb-4">Branch Icon</h3>
            <div className="flex items-center gap-4">
              {formData.iconUrl ? (
                <img
                  src={formData.iconUrl}
                  alt="Icon"
                  className="w-16 h-16 rounded-lg object-cover border-2 border-gray-200"
                />
              ) : (
                <div className="w-16 h-16 rounded-lg bg-gray-200 flex items-center justify-center">
                  <Building2 className="h-8 w-8 text-gray-400" />
                </div>
              )}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingImage}
                  className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  {uploadingImage ? 'Uploading...' : 'Upload icon'}
                </button>
                {formData.iconUrl && (
                  <button
                    type="button"
                    onClick={handleRemovePhoto}
                    className="text-gray-600 hover:text-gray-800 px-4 py-2 text-sm font-medium"
                  >
                    Remove icon
                  </button>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
              />
            </div>
            <p className="text-sm text-gray-500 mt-2">
              At least 32 × 32px PNG or JPG file.
            </p>
            {imageError && (
              <p className="text-sm text-red-600 mt-2">{imageError}</p>
            )}
          </div>

          {/* Branch Information */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-4">Branch Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Branch name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="e.g., Main Office"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Responsible person</label>
                <input
                  type="text"
                  value={formData.responsible}
                  onChange={e => setFormData({ ...formData, responsible: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Manager or responsible person"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Address *</label>
              <textarea
                value={formData.address}
                onChange={e => setFormData({ ...formData, address: e.target.value })}
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Full address of the branch"
                required
              />
            </div>
          </div>

          {/* Delete Section (only for editing) */}
          {editingSucursal && (
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-sm font-medium text-gray-900 mb-2">Danger Zone</h3>
              <p className="text-sm text-gray-600 mb-4">
                {sucursales.length === 1 
                  ? 'Cannot delete the last sucursal. At least one branch must exist.'
                  : 'Permanently delete this sucursal and all associated data.'}
              </p>
              <button
                type="button"
                onClick={() => deleteSucursal(editingSucursal._id)}
                disabled={sucursales.length === 1}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  sucursales.length === 1
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-red-600 hover:bg-red-700 text-white'
                }`}
              >
                Delete Sucursal
              </button>
            </div>
          )}

          {/* Action Buttons */}
          <div className="border-t border-gray-200 pt-6 flex justify-between">
            <button
              type="button"
              onClick={() => {
                setDrawerOpen(false);
                setEditingSucursal(null);
                setImageError('');
              }}
              className="px-6 py-2 text-gray-700 hover:text-gray-900 font-medium text-sm transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-lg font-medium text-sm transition-colors"
            >
              {editingSucursal ? 'Save Changes' : 'Create Sucursal'}
            </button>
          </div>
        </form>
      </DrawerWrapper>
    </div>
  );
}
